import { type VariantProps, tv } from "tailwind-variants";
import { twMerge } from "tailwind-merge";
import { forwardRef } from "react";

const buttonVariants = tv({
  base: [
    "inline-flex items-center justify-center rounded-md text-sm font-medium",
    "ring-offset-background transition-colors focus-visible:outline-hidden",
    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
  ],
  variants: {
    variant: {
      default: "bg-spkblue-base_color text-white hover:bg-spkblue-base_color2",
      destructive: "bg-spkred-100 text-white hover:bg-spkred-300",
      success: "bg-spkgreen-100 text-white hover:bg-spkgreen-150",
      cancel: "bg-spkgray-400 text-white hover:bg-spkgray-500",
      warning: "bg-orange-400 text-white hover:bg-orange-600/80",
      outline:
        "border border-spkgray-400 bg-background hover:bg-spkgray-100 dark:hover:text-black",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      ghost: "hover:bg-accent hover:text-accent-foreground",
      link: "text-primary underline-offset-4 hover:underline",
    },
    size: {
      default: "h-10 px-4 py-2",
      sm: "h-9 rounded-md px-3",
      lg: "h-11 rounded-md px-8",
      icon: "h-10 w-10",
    },
    isFullWidth: {
      true: "w-full",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
    isFullWidth: false,
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

/* Simple loading spinner using CSS animation */
const LoadingSpinner = () => (
  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
);

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      isFullWidth,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        className={twMerge(
          buttonVariants({ variant, size, isFullWidth, className })
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <LoadingSpinner />
            {children}
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
